import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  contentHeight,
  EnumTypes,
  ENV_CONFIG,
  OrderMode,
} from "../../utils/constants";
import {
  Delete,
  Dining,
  DirectionsRun,
  Print,
  Receipt,
  Sync,
} from "@mui/icons-material";
import { IOrder } from "../../types/IOrder";
import {
  formatDate,
  getItemBriefLabel,
  getItemLabel,
  printNewReceipt,
} from "../../utils/commonHelpers";
import { ICreateOrder } from "../../types/ICreateOrder";
import { ICreateOrderItem } from "../../types/ICreateOrderItem";
import { IItem } from "../../types/IItem";
import "./style.scss";
import Button from "../../components/Button";
import OrderItemCard from "./OrderItemCard";
import { IOrderBrief } from "../../types/IOrderBrief";
import { capitalize, debounce, isEmpty, upperCase } from "lodash";
import { IOrderSource } from "../../types/IOrderSource";
import useStore from "../../store";

const generateOrderCreationPayload = (
  paymentMode: string,
  orderItems: { item: IItem; quantity: number }[],
) => {
  const _orderItems: ICreateOrderItem[] = orderItems.map((orderItem) => ({
    quantity: orderItem.quantity,
    item_id: orderItem.item.id,
  }));

  return {
    order_items: _orderItems,
    notes: "",
    new_payment_mode: paymentMode,
  } as ICreateOrder;
};

let patching = false;
let praSyncing = false;

let cashTaken = 0;

const OrderTakingOld = () => {
  const [orderMode, setOrderMode] = useState<OrderMode>(OrderMode.DINEIN);
  const [paymentMode, setPaymentMode] = useState<string | null>(null);
  const isPaymentModeCash = paymentMode?.trim().toLowerCase() == "cash";

  const [itemsList, setItemsList] = useState<IItem[]>([]);
  const [orderItems, setOrderItems] = useState<
    { item: IItem; quantity: number }[]
  >([]);
  const [orderHistory, setOrderHistory] = useState<IOrder[]>();
  const [loading, setLoading] = useState(false);
  const [isCashTakenEntered, setIsCashTakenEntered] = useState(false);
  const [isPRAGstLoading, setIsPRAGstLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [orderBrief, setOrderBrief] = useState<IOrderBrief | null>(null);
  const {
    currentUserInfo,
    enums: {
      [EnumTypes.ORDER_SOURCE]: orderSources = [],
      [EnumTypes.PAYMENT_MODE]: paymentModes = [],
    },
  } = useStore();

  const paymentModeColorMapper = useMemo(
    () =>
      paymentModes.reduce(
        (acc, value: any, _) => {
          acc[value.value] = {
            bgcolor: value.bg_color,
            color: value.text_color,
          };
          return acc;
        },
        {} as { [key: string]: { bgcolor: string; color: string } },
      ),
    [paymentModes],
  );

  const isSuperUser = currentUserInfo?.is_superuser;

  const fetchAndSetOrderHistory = async () => {
    const response = await fetch(
      `${ENV_CONFIG.BACKEND_BASE_URL}/api/pos/order/`,
    );
    const data = await response.json();
    setOrderHistory(data.results);
  };

  const fetchAndSetItems = async () => {
    const response = await fetch(
      `${ENV_CONFIG.BACKEND_BASE_URL}/api/management/item/`,
    );
    const data = await response.json();
    setItemsList(data.results);
  };

  useEffect(() => {
    fetchAndSetItems();
    fetchAndSetOrderHistory();
  }, []);

  const findFromOrderItems = (orderItemItemId: string): number =>
    orderItems.findIndex((orderItem) => orderItem.item.id === orderItemItemId);

  const handleAddItem = (orderItemItemId: string) => {
    const orderItemIndex = findFromOrderItems(orderItemItemId);

    if (orderItemIndex >= 0) {
      let orderItem = orderItems[orderItemIndex];

      handleQuantityIncreaseDecrease(orderItemItemId, orderItem.quantity + 1);
    } else {
      const item = itemsList.find((item) => item.id === orderItemItemId);

      setOrderItems((prevItems) => [
        ...prevItems,
        { item: item!, quantity: 1 },
      ]);
    }
  };

  const handleQuantityIncreaseDecrease = (
    orderItemItemId: string,
    newQuantity: number,
  ) => {
    let orderItemIndex = findFromOrderItems(orderItemItemId);

    let orderItem = orderItems[orderItemIndex];

    if (newQuantity === 0) {
      handleDeleteItem(orderItemItemId);
      return;
    }

    orderItem = { ...orderItem, quantity: newQuantity };
    setOrderItems((prevItems) => [
      ...prevItems.slice(0, orderItemIndex),
      orderItem,
      ...prevItems.slice(orderItemIndex + 1),
    ]);
  };

  const handleDeleteItem = (orderItemItemId: string) => {
    const orderItemIndex = orderItems.findIndex(
      (orderItem) => orderItem.item.id === orderItemItemId,
    );

    setOrderItems((prevItems) => [
      ...prevItems.slice(0, orderItemIndex),
      ...prevItems.slice(orderItemIndex + 1),
    ]);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const orderPayload = generateOrderCreationPayload(
        paymentMode!,
        orderItems,
      );
      orderPayload.is_takeaway_order = orderMode === OrderMode.TAKEAWAY;

      const response = await fetch(
        `${ENV_CONFIG.BACKEND_BASE_URL}/api/pos/order/`,
        {
          method: "POST",
          body: JSON.stringify(orderPayload),
          headers: {
            "content-type": "application/json",
          },
        },
      );

      if (response.status === 201) {
        const data = await response.json();
        setOrderHistory((prevOrderHistory) => [
          data,
          ...(prevOrderHistory ?? []),
        ]);

        setPaymentMode(null);
        setOrderMode(OrderMode.DINEIN);
        setOrderItems([]);
        setOrderBrief(null);
        setIsCashTakenEntered(false);
        cashTaken = 0;
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const evaluateOrder = useCallback(
    debounce(async () => {
      try {
        if (!paymentMode) {
          return;
        }

        const orderPayload = generateOrderCreationPayload(
          paymentMode,
          orderItems,
        );

        const response = await fetch(
          `${ENV_CONFIG.BACKEND_BASE_URL}/api/pos/order/evaluate/`,
          {
            method: "POST",
            body: JSON.stringify(orderPayload),
            headers: {
              "content-type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          const data = await response.json();
          setOrderBrief(data);
        }
      } catch (error) {
        console.log("error: ", error);
      }
    }, 1200),
    [orderItems, paymentMode],
  );

  const pushOrderToPRA = async (orderId: string) => {
    try {
      if (praSyncing) {
        return;
      }
      praSyncing = true;

      setIsPRAGstLoading((prev) => ({
        ...prev,
        [orderId]: true,
      }));

      const response = await fetch(
        `${ENV_CONFIG.BACKEND_BASE_URL}/api/pos/order/${orderId}/pra_payload/`,
      );

      if (response.status === 200) {
        const { url, payload, headers } = await response.json();

        const praResponse = await fetch(url, {
          method: "POST",
          body: JSON.stringify(payload),
          headers: headers,
        });

        if (praResponse.status < 400) {
          const praJson = await praResponse.json();
          console.log("praResponse: ", praResponse);

          if (
            praJson.Code == "100" &&
            praJson.Response === "Invoice received successfully"
          ) {
            await patchOrder(orderId, {
              gst_invoice_id: praJson.InvoiceNumber,
            });
          }
        }
      }
    } catch (error) {
      console.log("pushOrderToPRA::error: ", error);
    } finally {
      setIsPRAGstLoading((prev) => ({
        ...prev,
        [orderId]: false,
      }));
      praSyncing = false;
    }
  };

  useEffect(() => {
    setOrderBrief(null);
    evaluateOrder();
    setIsCashTakenEntered(false);

    return () => evaluateOrder.cancel();
  }, [evaluateOrder]);

  const patchOrder = async (orderId: string, payload: { [x: string]: any }) => {
    if (patching) {
      return;
    }

    try {
      patching = true;
      await fetch(`${ENV_CONFIG.BACKEND_BASE_URL}/api/pos/order/${orderId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: { "content-type": "application/json" },
      });

      await fetchAndSetOrderHistory();
    } catch (error) {
      console.log("patchOrder:error: ", error);
    } finally {
      patching = false;
    }
  };

  const handleDeleteOrder = async (order: IOrder) => {
    const value = prompt(
      `Are you sure you want to delete this order? Type "yes" to delete it.`,
    );

    if (value?.trim().toLowerCase() === "yes") {
      await fetch(`${ENV_CONFIG.BACKEND_BASE_URL}/api/pos/order/${order.id}/`, {
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      });

      await fetchAndSetOrderHistory();
      alert(`Order#${order.id.split("-")[4]}  deleted successfully`);
    }
  };

  const handleTotalClick = (totalAmount: string) => {
    const takenByCustomer = prompt(
      `Order Amount: ${totalAmount} PKR\n\nHow much is taken by the customer?`,
    );

    if (takenByCustomer) {
      alert(`Giveback: ${Number(takenByCustomer) - Number(totalAmount)} PKR`);
    }
  };

  const handleSourceChange = (order: IOrder, nextSource: IOrderSource) => {
    let sourceIdentifier = null;

    if (nextSource.requires_identifier) {
      sourceIdentifier = prompt(
        `What is the source identifier for '${upperCase(nextSource.name)}'?`,
      );
      sourceIdentifier = (sourceIdentifier || "").trim();
    }

    if (
      (nextSource.requires_identifier && sourceIdentifier) ||
      !nextSource.requires_identifier
    ) {
      patchOrder(order.id, {
        new_source: nextSource.id,
        source_identifier: sourceIdentifier,
      });
    }
  };

  return (
    <Container id="order-taking-old-page-container">
      <Box display={"flex"} gap={2}>
        <Box className="past-orders-container">
          <TableContainer
            component={Paper}
            sx={{ height: contentHeight + 104 }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Order ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Items</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Created At</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Payment Mode</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Source</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Action</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* TODO: properly fix this orderSources thingy */}
                {orderSources.length > 0 &&
                  (orderHistory || []).map((order) => {
                    const orderSource = (orderSources.find(
                      (v) => v.id === order.source,
                    ) || {}) as IOrderSource;

                    const isGSTSorted = Boolean(order.gst_invoice_id);
                    const isGSTFree =
                      isGSTSorted &&
                      ["nil", "nill", "n/a"].includes(
                        order.gst_invoice_id!.toLowerCase(),
                      );

                    return (
                      <TableRow
                        key={order.id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {order.id.split("-")[4]} ({order.order_number})
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          {order.order_items
                            .map(
                              (v) =>
                                `${getItemBriefLabel(v.item)} x ${v.quantity}`,
                            )
                            .join(", ")}
                        </TableCell>
                        <TableCell
                          className="total-amount"
                          onClick={() => handleTotalClick(order.total)}
                        >
                          <abbr
                            title={`${order.price_without_gst} + ${order.gst_amount}`}
                          >
                            {order.total}
                          </abbr>
                        </TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            sx={
                              paymentModeColorMapper[order.payment_mode] || {}
                            }
                            label={capitalize(order.payment_mode)}
                            size="small"
                          />
                        </TableCell>

                        <TableCell className="source-cell">
                          <Select
                            id="order-source-dropdown"
                            value={orderSource.id}
                            onChange={(v) =>
                              handleSourceChange(
                                order,
                                orderSources.find(
                                  (x) => x.id === v.target.value,
                                )!,
                              )
                            }
                            size="small"
                            sx={{
                              bgcolor: orderSource.bg_color,
                              color: orderSource.text_color,
                              height: 26,
                              fontSize: "0.72rem",
                              borderRadius: "20px",
                              "& .MuiSelect-select": {
                                padding: "6px 14px !important",
                              },
                            }}
                            IconComponent={() => null}
                          >
                            {orderSources.map((v) => (
                              <MenuItem
                                key={v.id}
                                value={v.id}
                                disabled={!v.is_active}
                              >
                                {v.name}
                                {orderSource.id === v.id &&
                                v.requires_identifier
                                  ? `-${order.source_identifier}`
                                  : ""}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>

                        <TableCell align="right">
                          <abbr
                            title={
                              order.is_takeaway_order ? "Takeaway" : "Dine In"
                            }
                          >
                            {order.is_takeaway_order ? (
                              <DirectionsRun
                                className="icon"
                                color="error"
                                onClick={() =>
                                  patchOrder(order.id, {
                                    is_takeaway_order: false,
                                  })
                                }
                              />
                            ) : (
                              <Dining
                                className="icon"
                                color="primary"
                                onClick={() =>
                                  patchOrder(order.id, {
                                    is_takeaway_order: true,
                                  })
                                }
                              />
                            )}
                          </abbr>
                          {/* !order.gst_amount added to cater for old recrods */}
                          {isGSTSorted && !isGSTFree ? (
                            //  custoemr + inhouse receipt
                            <Print
                              className="icon print-icon"
                              onClick={() =>
                                printNewReceipt(order, orderSource)
                              }
                            />
                          ) : (
                            //  inhouse receipt only
                            <Receipt
                              className="icon print-icon"
                              onClick={() =>
                                printNewReceipt(order, orderSource, true)
                              }
                            />
                          )}

                          {!isGSTSorted && (
                            <Sync
                              color={
                                isPRAGstLoading[order.id]
                                  ? "disabled"
                                  : "inherit"
                              }
                              className="icon sync-icon"
                              onClick={() => pushOrderToPRA(order.id)}
                            />
                          )}

                          {!isGSTSorted && isSuperUser && (
                            <Delete
                              className="icon"
                              color="error"
                              onClick={() => handleDeleteOrder(order)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box className="new-order-container">
          <Box>
            <ToggleButtonGroup
              size="small"
              value={orderMode}
              onChange={(x: any) => setOrderMode(x.target.value)}
              fullWidth
            >
              {Object.entries(OrderMode).map(([_, label]) => (
                <ToggleButton
                  disabled={orderMode === label}
                  value={label}
                  key={label}
                  color={label === OrderMode.DINEIN ? "success" : "error"}
                >
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <ToggleButtonGroup
              size="small"
              value={paymentMode}
              onChange={(x: any) => setPaymentMode(x.target.value)}
              fullWidth
            >
              {paymentModes.map(({ label, value }) => {
                const isSelected = paymentMode === value;

                return (
                  <ToggleButton
                    disabled={isSelected}
                    value={value}
                    key={label}
                    sx={
                      isSelected
                        ? {
                            backgroundColor:
                              paymentModeColorMapper[value].bgcolor,
                            color: paymentModeColorMapper[value].color,
                          }
                        : {}
                    }
                    color={isSelected ? "primary" : "info"}
                  >
                    {label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>

          <Box id="order-taking-containers">
            <Autocomplete
              className="drinks-select"
              filterOptions={(options, params) => {
                return options.filter((option) =>
                  option.label
                    .toLowerCase()
                    .includes(params.inputValue.toLowerCase()),
                );
              }}
              value={null}
              options={itemsList.map((item) => ({
                label: getItemLabel(item),
                id: item.id,
              }))}
              renderInput={(params) => (
                <TextField {...params} placeholder="Choose items..." />
              )}
              onChange={(_, item) => handleAddItem(item!.id)}
            />

            <Box
              className="order-items-container"
              sx={{ height: contentHeight - (isPaymentModeCash ? 280 : 238) }}
            >
              {orderItems.map((orderItem) => (
                <OrderItemCard
                  key={orderItem.item.id}
                  orderItem={orderItem}
                  onQuantityChange={handleQuantityIncreaseDecrease}
                  onDeleteItem={handleDeleteItem}
                />
              ))}

              {orderItems.length === 0 && (
                <Card className="order-item-card tac" variant="outlined">
                  <CardContent>
                    <Typography
                      className="pt-16 pb-10 fwb"
                      color="textDisabled"
                      variant="subtitle1"
                      fontWeight={"bold"}
                    >
                      NO ITEMS ADDED
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

          <Box className="order-info">
            {/* <Box className="df jcsb mt-20">
              <Typography variant="body2">Payment Mode</Typography>
              <Typography variant="body2">
                <SelectField
                  size="small"
                  value={paymentMode}
                  options={paymentModes}
                  disabled={false}
                  onChange={setPaymentMode}
                />
              </Typography>
            </Box> */}
            <Box className="df jcsb mt-20">
              <Typography variant="body2">Sub-total</Typography>
              <Typography variant="body2">
                {Number(orderBrief?.price_without_gst ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <Box className="df jcsb mt-20">
              <Typography variant="body2">GST</Typography>
              <Typography variant="body2">
                {Number(orderBrief?.gst_amount ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <Box className="df jcsb mt-20">
              <Typography variant="body2">{`Total (After GST ${orderBrief?.gst_rate || 0}%)`}</Typography>
              <Typography variant="body2">
                {Number(orderBrief?.total ?? 0).toFixed(2)}
              </Typography>
            </Box>

            {isPaymentModeCash ? (
              <Box className="df jcsb mt-20">
                <Typography variant="body2">
                  {isCashTakenEntered ? "Giveback" : "Amount taken"}
                </Typography>

                {orderBrief?.total && isCashTakenEntered ? (
                  <Typography
                    variant="body2"
                    onClick={() => setIsCashTakenEntered(false)}
                  >
                    {(cashTaken - Number(orderBrief.total)).toFixed(2)}
                  </Typography>
                ) : (
                  <TextField
                    autoFocus
                    inputMode="numeric"
                    disabled={!orderBrief?.total}
                    type="number"
                    className="cash-given-input"
                    size="small"
                    onChange={(v) => (cashTaken = Number(v.target.value))}
                    onKeyUp={(event) => {
                      if (event.key === "Enter") {
                        if (cashTaken < Number(orderBrief?.total || 0)) {
                          alert(
                            "Cash taken can not be less than the order total.",
                          );
                        } else {
                          setIsCashTakenEntered(true);
                        }
                      }
                    }}
                  />
                )}
              </Box>
            ) : null}
          </Box>

          <Box className="action-button-container">
            <Button
              loading={loading}
              variant="contained"
              color="success"
              onClick={handleConfirm}
              disabled={
                orderItems.length === 0 ||
                (!isCashTakenEntered && isPaymentModeCash) ||
                !paymentMode ||
                isEmpty(orderBrief)
              }
            >
              CONFIRM
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default OrderTakingOld;
